/**
 * `/setup/isd` — ISD (school district) collection form
 * (web UI Phase 2 chunk 5/6).
 *
 * For each player on the captain's team, the captain can attach one or
 * more (`district_code`, `kid_count`) rows. The schema (PRD §6) supports
 * multiple districts per player (the Sasha-in-two-districts case), so
 * the UI is additive: one row at a time, never a multi-row form.
 *
 * Why an "Add another district" affordance per player (vs. a single
 * dropdown that toggles between Edit / Save):
 *   - Captains have one mental flow per row: pick the district, set the
 *     kid count, save. No accidental edits to a saved row.
 *   - Deletes are explicit (× per row) so the audit log captures intent.
 *   - The list re-fetches after each successful add — keeps the UI
 *     server-truth even if the captain has multiple tabs open.
 *
 * Chunk 6 (read-only /season view) is the next step after this; this
 * page's "Continue to season →" button navigates to /season/<team_id>.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, apiGet, apiPost } from "../lib/api";
import { useAuthedSession } from "../components/RequireAuth";

type PlayerDistrictRow = { district_code: string; kid_count: number };

type PlayerWithDistricts = {
  player_id: string;
  name: string;
  districts: PlayerDistrictRow[];
};

type ListResponse = { ok: true; players: PlayerWithDistricts[] };
type DistrictsListResponse = { ok: true; districts: string[] };

type LoadState =
  | { kind: "loading" }
  | { kind: "ok"; players: PlayerWithDistricts[]; districts: string[] }
  | { kind: "error"; message: string };

export default function SetupIsd() {
  const session = useAuthedSession();
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: "loading" });

  // Per-player UI state — which player currently has an "Add" form
  // expanded, plus the form values. Keyed by player_id so multiple
  // players can be expanded independently.
  const [openForms, setOpenForms] = useState<
    Record<string, { district_code: string; kid_count: string } | undefined>
  >({});
  const [savingFor, setSavingFor] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{
    player_id: string;
    message: string;
  } | null>(null);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    try {
      const [playersResp, districtsResp] = await Promise.all([
        apiGet<ListResponse>("/api/team/player-districts"),
        apiGet<DistrictsListResponse>("/api/districts"),
      ]);
      setState({
        kind: "ok",
        players: playersResp.players,
        districts: districtsResp.districts,
      });
    } catch (e) {
      // 404 = chunk-7 HTTP gateway not wired yet for one of these
      // endpoints. Render an explanatory banner rather than a hard error
      // so the page stays walkable during incremental Phase 2 landing.
      const msg =
        e instanceof ApiError
          ? e.status === 404
            ? "Backend route not yet available — chunk 5 wires the worker; chunk 7 wires the gateway. (Phase 2 in progress.)"
            : `Failed to load (${e.status}).`
          : "Network error. Check your connection and retry.";
      setState({ kind: "error", message: msg });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function openForm(player_id: string) {
    setOpenForms((cur) => ({
      ...cur,
      [player_id]: { district_code: "", kid_count: "1" },
    }));
    setRowError(null);
  }

  function closeForm(player_id: string) {
    setOpenForms((cur) => {
      const next = { ...cur };
      delete next[player_id];
      return next;
    });
    if (rowError && rowError.player_id === player_id) setRowError(null);
  }

  function setFormField(
    player_id: string,
    field: "district_code" | "kid_count",
    value: string,
  ) {
    setOpenForms((cur) => {
      const existing = cur[player_id] ?? { district_code: "", kid_count: "1" };
      return { ...cur, [player_id]: { ...existing, [field]: value } };
    });
  }

  async function onSave(player_id: string) {
    const form = openForms[player_id];
    if (!form) return;
    const district_code = form.district_code.trim();
    const kid_count = Number(form.kid_count);
    if (district_code.length < 2) {
      setRowError({
        player_id,
        message: "Pick a district from the list.",
      });
      return;
    }
    if (!Number.isInteger(kid_count) || kid_count < 1 || kid_count > 10) {
      setRowError({
        player_id,
        message: "Number of kids must be a whole number from 1 to 10.",
      });
      return;
    }
    setSavingFor(player_id);
    setRowError(null);
    try {
      await apiPost(
        `/api/team/players/${encodeURIComponent(player_id)}/districts`,
        { district_code, kid_count },
      );
      closeForm(player_id);
      await load();
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.status === 404
            ? "This player isn't on your team."
            : e.status === 400
              ? "Invalid input — check the district code + kid count."
              : `Save failed (${e.status}).`
          : "Network error. Retry.";
      setRowError({ player_id, message: msg });
    } finally {
      setSavingFor(null);
    }
  }

  async function onDelete(player_id: string, district_code: string) {
    setSavingFor(player_id);
    setRowError(null);
    try {
      const res = await fetch(
        `/api/team/players/${encodeURIComponent(
          player_id,
        )}/districts/${encodeURIComponent(district_code)}`,
        { method: "DELETE", credentials: "include" },
      );
      if (!res.ok) {
        throw new ApiError(res.status, `DELETE → ${res.status}`);
      }
      await load();
    } catch (e) {
      const msg =
        e instanceof ApiError ? `Delete failed (${e.status}).` : "Network error.";
      setRowError({ player_id, message: msg });
    } finally {
      setSavingFor(null);
    }
  }

  return (
    <section>
      <h1>Where do players&apos; kids go to school?</h1>
      <p className="muted">
        Lets nofuzz cross-reference school calendars against your match dates
        to spot conflicts.
      </p>

      {state.kind === "loading" && <p className="muted">Loading roster…</p>}

      {state.kind === "error" && (
        <p role="alert" style={{ color: "#b3261e" }}>
          {state.message}
        </p>
      )}

      {state.kind === "ok" && state.players.length === 0 && (
        <p className="muted">
          No players on your team yet. Finish <code>/setup</code> first, then
          come back here.
        </p>
      )}

      {state.kind === "ok" &&
        state.players.map((p) => {
          const form = openForms[p.player_id];
          const isSaving = savingFor === p.player_id;
          const errForRow =
            rowError && rowError.player_id === p.player_id
              ? rowError.message
              : null;
          return (
            <div
              key={p.player_id}
              style={{ marginTop: "1.5rem", borderTop: "1px solid #e5e5e5", paddingTop: "1rem" }}
            >
              <h3 style={{ margin: 0 }}>{p.name}</h3>
              {p.districts.length === 0 ? (
                <p className="muted">
                  <small>No districts on file yet.</small>
                </p>
              ) : (
                <ul style={{ listStyle: "none", padding: 0, margin: "0.5rem 0" }}>
                  {p.districts.map((d) => (
                    <li
                      key={d.district_code}
                      style={{
                        display: "flex",
                        gap: "0.75rem",
                        alignItems: "center",
                        padding: "0.25rem 0",
                      }}
                    >
                      <span>
                        <strong>{d.district_code}</strong> · {d.kid_count} kid
                        {d.kid_count === 1 ? "" : "s"}
                      </span>
                      <button
                        type="button"
                        className="button button-ghost"
                        aria-label={`Remove ${d.district_code}`}
                        onClick={() => onDelete(p.player_id, d.district_code)}
                        disabled={isSaving}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {form ? (
                <div
                  style={{
                    background: "#f6f6f6",
                    padding: "0.75rem",
                    borderRadius: 4,
                    marginTop: "0.5rem",
                  }}
                >
                  <p style={{ margin: "0 0 0.5rem" }}>
                    <label htmlFor={`dc-${p.player_id}`}>District</label>{" "}
                    <input
                      id={`dc-${p.player_id}`}
                      list={`districts-${p.player_id}`}
                      type="text"
                      value={form.district_code}
                      onChange={(e) =>
                        setFormField(p.player_id, "district_code", e.target.value)
                      }
                      placeholder="AISD"
                      style={{ padding: "0.25rem 0.5rem", marginLeft: "0.5rem" }}
                    />
                    <datalist id={`districts-${p.player_id}`}>
                      {state.districts.map((d) => (
                        <option key={d} value={d} />
                      ))}
                    </datalist>
                  </p>
                  <p style={{ margin: "0 0 0.5rem" }}>
                    <label htmlFor={`kc-${p.player_id}`}>Kids in this district</label>{" "}
                    <input
                      id={`kc-${p.player_id}`}
                      type="number"
                      min={1}
                      max={10}
                      step={1}
                      value={form.kid_count}
                      onChange={(e) =>
                        setFormField(p.player_id, "kid_count", e.target.value)
                      }
                      style={{
                        padding: "0.25rem 0.5rem",
                        marginLeft: "0.5rem",
                        width: "5rem",
                      }}
                    />
                  </p>
                  <p style={{ margin: 0 }}>
                    <button
                      type="button"
                      className="button"
                      onClick={() => onSave(p.player_id)}
                      disabled={isSaving}
                    >
                      {isSaving ? "Saving…" : "Save"}
                    </button>{" "}
                    <button
                      type="button"
                      className="button button-ghost"
                      onClick={() => closeForm(p.player_id)}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                  </p>
                  {errForRow && (
                    <p role="alert" style={{ color: "#b3261e", margin: "0.5rem 0 0" }}>
                      {errForRow}
                    </p>
                  )}
                </div>
              ) : (
                <p style={{ margin: "0.5rem 0 0" }}>
                  <button
                    type="button"
                    className="button button-ghost"
                    onClick={() => openForm(p.player_id)}
                  >
                    + Add another district
                  </button>
                </p>
              )}
            </div>
          );
        })}

      {state.kind === "ok" && (
        <p style={{ marginTop: "2rem" }}>
          <button
            type="button"
            className="button"
            onClick={() => navigate(`/season/${session.team_id}`)}
          >
            Continue to season →
          </button>
        </p>
      )}
    </section>
  );
}
